import { useState, useEffect, useRef, useContext, useTransition } from 'react'
import { flushSync } from 'react-dom'
import ApplicationContext from '@platform/react/context/application'

/**
 * useSockets hook
 *
 * Connects to websocket according to `events.list` and `documentType`. Whenever a
 * websocket message is obtained through the corresponding channel, uses
 * `events.onPublication` to alter `startData`.
 *
 * The websocket connection is reset whenever `deps` change.
 *
 * @param {Object[]} startData            the list to modify according to the websocket messages
 * @param {Object} events                 hook events
 * @param {function} events.list          a function that returns the first part of the socket key
 * @param {function} events.onPublication a function that returns the altered item and a function to
 *                                        alter the list with it
 * @param {string} documentType           the second part of the socket key
 * @param {string} [namespace='ref']      sockets' namespace to listen to
 * @param {array} [deps]                  an array of dependencies to reset the connection
 * @returns {{data: object[]}}
 */
const useSockets = (startData, { events, documentType, namespace = 'ref' }, deps = []) => {
  const [, startTransition] = useTransition()
  const { socket: { [namespace]: socket } } = useContext(ApplicationContext)
  const socketKey = useRef()
  const [data, setData] = useState(startData)

  useEffect(() => {
    const parentRef = events.list()
    socketKey.current = documentType ? `${parentRef}_${documentType}` : parentRef
    const listener = async (e) => {
      const { item, addItem } = await events.onPublication(e)
      flushSync(() => {
        setData(prevData => (addItem && item ? addItem(prevData, item) : prevData))
      })
    }
    socket.sub(socketKey.current, () => {
      socket.on(socketKey.current, listener)
    })
    return () => socket.unsub(socketKey.current, () => {
      socket.off(socketKey.current, listener)
    })
  }, deps)

  useEffect(() => {
    startTransition(() => {
      setData(startData)
    })
  }, [startData])

  return { data }
}

export default useSockets
